interface UpdateElementParams {
  newContent: string
  /** 
   * Static css url. Use for add <link> tag to head
   */
  cssLink?: string
  /** 
   * Dynamic css import. Usually this function returned from import.meta.glob()
   */
  cssImport?: () => Promise<void>
}

export const loadStaticCSS = (href: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`link[href="${href}"]`)) return resolve();
  
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject();

    document.head.appendChild(link);
  });
}

/**
 * Injects CSS content into the document head as a <style> tag.
 * 
 * @param cssContent - The CSS content to inject
 * @param selector - Optional ID selector (e.g., '#my-style-id' or 'my-style-id'). 
 *                   If provided, checks if an element with this id already exists.
 *                   If it exists, the function returns early without injecting.
 *                   If it doesn't exist, creates a new <style> element with this id (removes # prefix if present).
 *                   If not provided, creates a new <style> element without an id.
 */
export const injectCss = (cssContent: string, selectorId?: string): void => {
  if (!cssContent || typeof cssContent !== 'string' || !cssContent.trim()) {
    return;
  }

  const selector = typeof selectorId === 'string'? `#${selectorId.replace(/^#/, '')}` : '';
  if (selector && document.getElementById(selector)) {
    return;
  }

  const styleElement = document.createElement('style');
  
  if (selector) {
    styleElement.id = selector;
  }
  
  styleElement.textContent = cssContent;
  document.head.appendChild(styleElement);
};

export const toggleElement = (subject: string | HTMLElement, forceState?: boolean): void => {
  const el = typeof subject === 'string' ? document.querySelector(subject) : subject;
  if (!el || !(el instanceof HTMLElement)) return;

  if (forceState !== undefined) {
    el.style.display = forceState ? '' : 'none';
    return;
  }

  if (el.style.display === 'none') {
    el.style.display = '';
  } else {
    el.style.display = 'none';
  }
}

export const updateElement = async (subject: string | HTMLElement, { newContent, cssLink, cssImport }: UpdateElementParams) => {
  const el = typeof subject === 'string' ? document.querySelector(subject) : subject;
  if (!el || !(el instanceof HTMLElement)) return;

  el.innerHTML = newContent;

  if (cssLink) {
    loadStaticCSS(cssLink);
  }

  if (cssImport) {
    await cssImport();
  }

  toggleElement(el, newContent.length > 0);
};
